import { gql, useLazyQuery } from "@outschool/ui-apollo";
import { useMemo } from "react";

import {
  SearchSuggestionsQuery,
  SearchSuggestionsQueryVariables,
} from "../../generated/graphql";
import { useSearchSuggestionsContext } from "../providers/SearchSuggestionsProvider";

import type { SearchItem } from "@outschool/ui-components-classroom";

export const LearnerSearchSuggestions = gql`
  query SearchSuggestions($query: String!, $size: Int) {
    searchSuggestions(search: { query: $query }, size: $size) {
      topicSuggestions {
        uid
        label
        category
      }
      teacherSuggestions {
        uid
        name
        photo
        leader_link
      }
    }
  }
`;

export function useSearchSuggestions(size?: number) {
  const [fetchSuggestions, { data, loading, error }] = useLazyQuery<
    SearchSuggestionsQuery,
    SearchSuggestionsQueryVariables
  >(LearnerSearchSuggestions, { variables: { query: "", size } });

  return {
    loading,
    error,
    fetchSuggestions,
    topicSuggestions: data?.searchSuggestions?.topicSuggestions,
    teacherSuggestions: data?.searchSuggestions?.teacherSuggestions,
  };
}

/**
 * Computes search suggestions as a memo based on input and fetched data.
 */
export function useSearchSuggestionsData({
  topicSuggestions,
  teacherSuggestions,
  inputValue,
}: {
  topicSuggestions?: SearchSuggestionsQuery["searchSuggestions"]["topicSuggestions"];
  teacherSuggestions?: SearchSuggestionsQuery["searchSuggestions"]["teacherSuggestions"];
  inputValue: string;
}): SearchItem[] {
  return useMemo((): SearchItem[] => {
    const suggestions: SearchItem[] = [];

    topicSuggestions?.forEach(t => {
      suggestions.push({
        uid: t.uid,
        name: t.label,
        category: t.category ?? undefined,
        type: "topic",
      });
    });

    teacherSuggestions?.forEach(t => {
      suggestions.push({
        uid: t.uid,
        name: t.name || "",
        type: "teacher",
        photo: t.photo,
        leaderLink: t.leader_link,
      });
    });

    suggestions.push({
      uid: "keyword",
      name: inputValue,
      type: "keyword",
    });

    return suggestions;
  }, [topicSuggestions, teacherSuggestions, inputValue]);
}

/**
 * Hook to get initial search item from context.
 * Use this to initialize combobox state instead of effects.
 */
export function useInitialSearchItem() {
  const searchSuggestionsContext = useSearchSuggestionsContext();
  return searchSuggestionsContext?.initialSelectedItem;
}
